import React, { useState } from 'react';
import { ArrowLeft, CheckCircle, XCircle, AlertTriangle, Save } from 'lucide-react';

// Matches the palette already used across NeuroWiseScreening.jsx
const COLORS = {
  navyBlue: '#003366',
  orange: '#FF6B35',
  mustard: '#FFAA00',
  green: '#16A34A',
  red: '#DC2626',
  lightGray: '#F9FAFB',
  white: '#FFFFFF',
  borderGray: '#D1D5DB',
  textGray: '#6B7280',
  textDark: '#1F2937',
};

const DISCONTINUATION_ERROR_COUNT = 10;

const fetchWithTimeout = (url, options = {}, timeoutMs = 30000) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  const token = window.sessionStorage.getItem('nwaAccessToken');
  const headers = { ...(options.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;
  return fetch(url, { ...options, headers, signal: controller.signal }).finally(() => clearTimeout(timer));
};

const todayDDMMYYYY = () => {
  const d = new Date();
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
};

const styles = {
  container: { minHeight: '100vh', backgroundColor: COLORS.lightGray },
  header: {
    backgroundColor: COLORS.navyBlue,
    color: COLORS.white,
    padding: '8px 16px',
    borderBottom: `2px solid ${COLORS.mustard}`,
  },
  headerContent: {
    maxWidth: '900px',
    margin: '0 auto',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  backButton: {
    backgroundColor: 'transparent',
    border: 'none',
    color: COLORS.white,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    padding: '8px 0',
  },
  body: { maxWidth: '760px', margin: '0 auto', padding: '24px' },
  card: {
    backgroundColor: COLORS.white,
    borderRadius: '8px',
    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
    padding: '24px',
    marginBottom: '20px',
  },
  label: { display: 'block', fontSize: '14px', fontWeight: '600', color: COLORS.textDark, marginBottom: '6px' },
  input: {
    width: '100%',
    padding: '10px 14px',
    border: `2px solid ${COLORS.borderGray}`,
    borderRadius: '6px',
    fontSize: '15px',
    boxSizing: 'border-box',
    marginBottom: '16px',
  },
  select: {
    width: '100%',
    padding: '10px 14px',
    border: `2px solid ${COLORS.borderGray}`,
    borderRadius: '6px',
    fontSize: '15px',
    backgroundColor: COLORS.white,
    boxSizing: 'border-box',
    marginBottom: '16px',
  },
  button: {
    backgroundColor: COLORS.orange,
    color: COLORS.white,
    padding: '12px 24px',
    borderRadius: '6px',
    fontWeight: '600',
    border: 'none',
    cursor: 'pointer',
    fontSize: '16px',
  },
  secondaryButton: {
    backgroundColor: COLORS.white,
    color: COLORS.navyBlue,
    padding: '12px 24px',
    borderRadius: '6px',
    fontWeight: '600',
    border: `2px solid ${COLORS.navyBlue}`,
    cursor: 'pointer',
    fontSize: '16px',
  },
  errorBox: {
    backgroundColor: '#FEE2E2',
    border: '1px solid #FCA5A5',
    color: '#991B1B',
    padding: '12px 16px',
    borderRadius: '6px',
    marginBottom: '16px',
  },
};

/**
 * Props:
 *   student          { student_code, full_name, dob, ... }  (from parent app)
 *   apiUrl            base API URL, e.g. https://neurowise-screening.onrender.com
 *   facilitatorId     current logged in facilitator id
 *   facilitatorName   current logged in facilitator name
 *   onBack()          called when the practitioner leaves without saving
 *   onSaved(result)   called after a successful save, with the API response
 */
export default function Hast2Screening({ student, apiUrl, facilitatorId, facilitatorName, schoolId, schoolName, programmeId, programmeName, initialTimepoint, onBack, onSaved }) {
  const [stage, setStage] = useState('setup'); // setup | administering | review | complete
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // --- setup fields ---
  const [form, setForm] = useState('A');
  const [screeningDate, setScreeningDate] = useState(todayDDMMYYYY());
  // Timepoint is chosen on the dashboard's Timeline card before this
  // screen is ever reached, so it's fixed for the whole session, not
  // editable here.
  const timepoint = initialTimepoint || 'Baseline';
  const [administrationMode, setAdministrationMode] = useState('Individual');
  const administrationBatchId = ''; // kept blank internally; no practitioner entry needed
  // schoolName now comes in as a prop, known from the teacher's own
  // school access, so there's nothing to enter here anymore.
  // programmeName now comes in as a prop too, known from the student's
  // own registration, so there's nothing to enter here anymore.
  const [assessorName, setAssessorName] = useState(facilitatorName || '');

  // --- administration state ---
  const [itemCount, setItemCount] = useState(null);
  const [currentItem, setCurrentItem] = useState(null); // {item_number, item_code, target_word}
  const [responses, setResponses] = useState([]); // ordered list, one per item shown so far
  const [spelledAs, setSpelledAs] = useState('');
  const [discontinuedAt, setDiscontinuedAt] = useState(null); // item_number of the 10th error, once reached
  const [continueBeyondDiscontinuation, setContinueBeyondDiscontinuation] = useState(false);

  // --- review state ---
  const [scoring, setScoring] = useState(null);
  const [notes, setNotes] = useState('');

  const errorCount = responses.filter((r) => !r.is_correct).length;

  const startAdministration = async () => {
    setError('');
    setLoading(true);
    try {
      const res = await fetchWithTimeout(`${apiUrl}/api/hast2/meta/${form}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not load spelling item count');
      setItemCount(data.item_count);
      await loadItem(1);
      setStage('administering');
    } catch (e) {
      setError(e.message || 'Could not start the administration');
    } finally {
      setLoading(false);
    }
  };

  const loadItem = async (itemNumber) => {
    const res = await fetchWithTimeout(`${apiUrl}/api/hast2/item/${form}/${itemNumber}`);
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || `Could not load item ${itemNumber}`);
    setCurrentItem(data);
    setSpelledAs('');
  };

  const recordResponse = async (isCorrect) => {
    if (!currentItem) return;
    setError('');
    const entry = {
      item_number: currentItem.item_number,
      item_code: currentItem.item_code,
      target_word: currentItem.target_word,
      is_correct: isCorrect,
      student_response: isCorrect ? '' : spelledAs.trim(),
    };
    const nextResponses = [...responses, entry];
    setResponses(nextResponses);

    const newErrorCount = nextResponses.filter((r) => !r.is_correct).length;
    const justHitDiscontinuation = newErrorCount === DISCONTINUATION_ERROR_COUNT && discontinuedAt === null;
    if (justHitDiscontinuation) {
      setDiscontinuedAt(currentItem.item_number);
    }

    const reachedEnd = currentItem.item_number >= itemCount;
    const shouldStop = reachedEnd || justHitDiscontinuation || discontinuedAt !== null;

    if (shouldStop) {
      await goToReview(nextResponses);
      return;
    }


    setLoading(true);
    try {
      await loadItem(currentItem.item_number + 1);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const continueAfterDiscontinuation = async () => {
    setContinueBeyondDiscontinuation(true);
    setLoading(true);
    setError('');
    try {
      await loadItem(currentItem.item_number + 1);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const stopAtDiscontinuation = async () => {
    await goToReview(responses);
  };

  const goToReview = async (finalResponses) => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchWithTimeout(`${apiUrl}/api/hast2/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          student_code: student.student_code,
          form,
          screening_date: screeningDate,
          responses: finalResponses,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not score this administration');
      setScoring(data.scoring);
      setStage('review');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const submitAndLock = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchWithTimeout(`${apiUrl}/api/hast2/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          student_code: student.student_code,
          form,
          screening_date: screeningDate,
          timepoint,
          administration_mode: administrationMode,
          administration_batch_id: administrationBatchId,
          school_id: schoolId || student?.school_id || '',
          school_name: schoolName,
          programme_id: programmeId || student?.programme_id || '',
          programme_name: programmeName,
          assessor_name: assessorName,
          entered_by_user_id: facilitatorId,
          entered_by_name: facilitatorName,
          notes,
          responses,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not save this administration');
      setStage('complete');
      if (onSaved) onSaved(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const Header = ({ title }) => (
    <div style={styles.header}>
      <div style={styles.headerContent}>
        <button style={styles.backButton} onClick={onBack}>
          <ArrowLeft size={18} />
          Back to {student.full_name}
        </button>
        <span style={{ fontSize: '13px', color: COLORS.mustard, fontWeight: '600' }}>{title}</span>
      </div>
    </div>
  );

  // ---- SETUP ----
  if (stage === 'setup') {
    return (
      <div style={styles.container}>
        <Header title="Spellings: Set up" />
        <div style={styles.body}>
          <div style={styles.card}>
            <h1 style={{ fontSize: '22px', fontWeight: 'bold', color: COLORS.navyBlue, marginBottom: '20px' }}>
              Spellings - {student.full_name}
            </h1>

            {error && <div style={styles.errorBox}>{error}</div>}

            <label style={styles.label}>Form</label>
            <select style={styles.select} value={form} onChange={(e) => setForm(e.target.value)}>
              <option value="A">Form A</option>
              <option value="B">Form B</option>
            </select>

            <label style={styles.label}>Screening date (DD/MM/YYYY)</label>
            <input
              style={styles.input}
              type="text"
              value={screeningDate}
              onChange={(e) => setScreeningDate(e.target.value)}
              placeholder="DD/MM/YYYY"
            />

            <label style={styles.label}>Administration mode</label>
            <select
              style={styles.select}
              value={administrationMode}
              onChange={(e) => setAdministrationMode(e.target.value)}
            >
              <option value="Individual">Individual (stop at discontinuation)</option>
              <option value="Group">Group</option>
            </select>


            <label style={styles.label}>Assessor name</label>
            <input
              style={styles.input}
              type="text"
              value={assessorName}
              onChange={(e) => setAssessorName(e.target.value)}
            />

            <button style={styles.button} onClick={startAdministration} disabled={loading}>
              {loading ? 'Loading…' : 'Start administration'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---- ADMINISTERING ----
  if (stage === 'administering') {
    const spelledAsTrimmed = spelledAs.trim();
    const matchesTarget = !!spelledAsTrimmed && !!currentItem &&
      spelledAsTrimmed.toLowerCase() === currentItem.target_word.trim().toLowerCase();
    const mismatchesTarget = !!spelledAsTrimmed && !!currentItem && !matchesTarget;
    const showDiscontinuationChoice = false;

    return (
      <div style={styles.container}>
        <Header title={`Spellings - Item ${currentItem ? currentItem.item_number : ''} of ${itemCount || ''}`} />
        <div style={styles.body}>
          <div style={styles.card}>
            {error && <div style={styles.errorBox}>{error}</div>}

            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '20px',
                fontSize: '14px',
                color: COLORS.textGray,
              }}
            >
              <span>Errors so far: <strong style={{ color: errorCount >= 10 ? COLORS.red : COLORS.textDark }}>{errorCount}</strong> / 10</span>
              {discontinuedAt !== null && (
                <span style={{ color: COLORS.red, fontWeight: '600' }}>
                  Discontinuation reached at item {discontinuedAt}
                  {continueBeyondDiscontinuation ? ' — recording Beyond Discontinuation items' : ''}
                </span>
              )}
            </div>

            {showDiscontinuationChoice ? (
              <div>
                <div
                  style={{
                    backgroundColor: '#FEF3C7',
                    border: '1px solid #FCD34D',
                    borderRadius: '6px',
                    padding: '16px',
                    marginBottom: '20px',
                    display: 'flex',
                    gap: '12px',
                    alignItems: 'flex-start',
                  }}
                >
                  <AlertTriangle size={22} color="#92400E" />
                  <div style={{ fontSize: '14px', color: '#92400E' }}>
                    The 10th error has been reached, at item {discontinuedAt}. The formal score is fixed from here.
                    Since this is a group administration, you can either stop now or keep going and record any
                    further errors as Beyond Discontinuation for the record. Either way, the raw score and standard
                    score will not change.
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button style={styles.button} onClick={stopAtDiscontinuation} disabled={loading}>
                    Stop here
                  </button>
                  <button style={styles.secondaryButton} onClick={continueAfterDiscontinuation} disabled={loading}>
                    Continue recording
                  </button>
                </div>
              </div>
            ) : (
              currentItem && (
                <div>
                  <p style={{ fontSize: '13px', color: COLORS.textGray, marginBottom: '4px' }}>Target word</p>
                  <div
                    style={{
                      fontSize: '32px',
                      fontWeight: 'bold',
                      color: COLORS.navyBlue,
                      padding: '16px',
                      backgroundColor: COLORS.lightGray,
                      borderRadius: '8px',
                      textAlign: 'center',
                      marginBottom: '20px',
                    }}
                  >
                    {currentItem.target_word}
                  </div>

                  <label style={styles.label}>If incorrect: type the wrongly spelt word, then mark Incorrect</label>
                  <input
                    style={styles.input}
                    type="text"
                    value={spelledAs}
                    onChange={(e) => setSpelledAs(e.target.value)}
                    placeholder="What the student actually wrote"
                  />


                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                      style={{ ...styles.button, backgroundColor: COLORS.green, display: 'flex', alignItems: 'center', gap: '8px' }}
                      onClick={() => recordResponse(true)}
                      disabled={loading || mismatchesTarget}
                    >
                      <CheckCircle size={18} /> Correct
                    </button>
                    <button
                      style={{ ...styles.button, backgroundColor: COLORS.red, display: 'flex', alignItems: 'center', gap: '8px' }}
                      onClick={() => recordResponse(false)}
                      disabled={loading || !spelledAsTrimmed || matchesTarget}
                    >
                      <XCircle size={18} /> Incorrect
                    </button>
                  </div>
                </div>
              )
            )}
          </div>
        </div>
      </div>
    );
  }

  // ---- REVIEW ----
  if (stage === 'review' && scoring) {
    const ss = scoring.standard_score;
    const ci = scoring.confidence_interval;
    const patoss = scoring.patoss;

    return (
      <div style={styles.container}>
        <Header title="Spellings: Review before submitting" />
        <div style={styles.body}>
          <div style={styles.card}>
            {error && <div style={styles.errorBox}>{error}</div>}

            <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: COLORS.navyBlue, marginBottom: '16px' }}>
              Result summary
            </h2>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px', fontSize: '14px' }}>
              <div><strong>Chronological age:</strong> {scoring.chronological_age.display}</div>
              <div><strong>Items administered:</strong> {scoring.items_administered}</div>
              <div><strong>Raw score:</strong> {scoring.raw_score}</div>
              <div><strong>Error count:</strong> {scoring.error_count}</div>
              <div><strong>Standard score:</strong> {ss.display ?? 'Not available'}</div>
              <div><strong>Age equivalent:</strong> {scoring.age_equivalent || 'Not available'}</div>
              <div>
                <strong>95% CI:</strong> {ci ? `${ci.ci_95[0]} to ${ci.ci_95[1]}` : 'Not applicable (bounded score)'}
              </div>
              <div><strong>Percentile:</strong> {patoss.percentile_display || 'Not available'}</div>
              <div style={{ gridColumn: '1 / -1' }}>
                <strong>Band descriptor:</strong> {patoss.descriptor || 'Not available'}
              </div>
            </div>

            {(ss.out_of_table || patoss.out_of_table) && (
              <div style={styles.errorBox}>
                This raw score / age combination fell outside the printed norms table and needs manual review
                before the result is treated as final.
              </div>
            )}

            <label style={styles.label}>Recommendations / notes (optional)</label>
            <textarea
              style={{ ...styles.input, minHeight: '80px', fontFamily: 'inherit' }}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                style={{ ...styles.button, display: 'flex', alignItems: 'center', gap: '8px' }}
                onClick={submitAndLock}
                disabled={loading}
              >
                <Save size={18} /> {loading ? 'Saving…' : 'Submit & Lock'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---- COMPLETE ----
  if (stage === 'complete') {
    return (
      <div style={styles.container}>
        <Header title="Spellings: Saved" />
        <div style={styles.body}>
          <div style={styles.card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: COLORS.green, marginBottom: '16px' }}>
              <CheckCircle size={28} />
              <h2 style={{ fontSize: '20px', fontWeight: 'bold' }}>Saved</h2>
            </div>
            <p style={{ color: COLORS.textGray, marginBottom: '20px' }}>Spellings have been recorded.</p>
            <button style={styles.button} onClick={onBack}>
              Back to {student.full_name}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
